import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack, Tooltip } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { Badge } from "@src/appV2/redesign/components/Badge";
import { Button, type ButtonVariant } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { BunnyLogo, ClipboardLogo } from "@src/appV2/redesign/components/Logos";
import { useIsRebrand } from "@src/appV2/redesign/theme/RebrandProvider";
import { FacilityTypes } from "@src/appV2/redesign/utils/facility";
import { routes } from "@src/appV2/redesign/utils/routes";
import { NotificationsCenterDrawer } from "@src/appV2/redesign/AppLayout/Shared/NotificationsCenterDrawer";
import { ThemeModeToggle } from "@src/appV2/UserPreferences/components/ThemeModeToggle";
import { useThemeMode } from "@src/appV2/UserPreferences/UserPreferencesProvider";
import FacilityDrawer from "src/containers/accountManagement/facilityDrawer";
import WorkerDrawerUntyped from "src/containers/accountManagement/agentsOldUX/workerDrawer";
import { useFlags } from "launchdarkly-react-client-sdk";
import { type ComponentType, useState } from "react";
import { Link } from "react-router-dom";

import { AccountSwitcherDesktop } from "../AccountSwitcher/AccountSwitcher";
import { useSwitchToAdmin } from "../../Shared/AccountSwitcher/useSwitchToAdmin";
import { useNavHeader } from "../../Shared/Header/useNavHeader";
import { EnvironmentChip } from "../../Shared/Header/EnvironmentChip";
import { SessionContextChip } from "../../Shared/Header/SessionContextChip";
import { ContactSupportButton } from "../../Shared/Header/ContactSupportButton";

// The WorkerDrawer default export is wrapped in withLDConsumer/withRouter/
// connect, which erase the class's own props from the public type. Assert
// the shape this caller supplies so prop-checking works across any
// formatter-imposed line breaks (a @ts-expect-error on the JSX is fragile).
const WorkerDrawer = WorkerDrawerUntyped as unknown as ComponentType<{
  userId: string | undefined;
  closeDrawer: () => void;
  ldFlags: ReturnType<typeof useFlags>;
}>;

export function AppLayoutHeaderDesktop() {
  const {
    displayHelpButton,
    iconTypeHelpButton,
    onClickHelpButton,

    displayNotificationsButton,
    iconTypeNotificationsButton,
    unreadNotificationCount,
    notificationsCenterModalState,
    onClickNotificationsButton,
  } = useNavHeader();

  const session = useSession();
  const { admin, profile, userId, type: sessionType } = session;
  const { isImpersonating, switchToAdmin } = useSwitchToAdmin();
  const isRebrand = useIsRebrand();
  const { themeMode } = useThemeMode();
  const isDark = themeMode === "dark";
  const ldFlags = useFlags();
  const [isWorkplaceDrawerOpen, setIsWorkplaceDrawerOpen] = useState(false);
  const [isWorkerDrawerOpen, setIsWorkerDrawerOpen] = useState(false);
  const isImpersonatingWorker = isImpersonating && sessionType === "AGENT";
  const isImpersonatingWorkplace = isImpersonating && sessionType === "FACILITY";

  let onContextChipClick: (() => void) | undefined;
  if (isImpersonatingWorker) {
    onContextChipClick = () => setIsWorkerDrawerOpen(true);
  } else if (isImpersonatingWorkplace) {
    onContextChipClick = () => setIsWorkplaceDrawerOpen(true);
  }
  // On the dark navbar, the rebrand "link" color (dark brown) would sit on a near-black
  // surface — flip to the inverted link variant so icons/labels keep contrast.
  const navLinkVariant: ButtonVariant = isRebrand && !isDark ? "link" : "linkInverted";
  const isEducation =
    profile?.type === FacilityTypes.SCHOOL || profile?.type === FacilityTypes.CHILD_CARE_CENTER;

  const basePath = process.env.REACT_APP_BASE_PATH || "/";
  const rebrandWordmark =
    themeMode === "dark"
      ? `${basePath}assets/clipboard-wordmark-yellow.png`
      : `${basePath}assets/clipboard-wordmark-brown.png`;
  const JUST_CLIPBOARD_LOGO_URL = isRebrand ? rebrandWordmark : `${basePath}justClipboard.png`;
  const BUNNY_EDUCATION_LOGO_URL = `${basePath}bunnyEducation.png`;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      spacing={5}
      paddingLeft={isRebrand ? "20px" : 5}
      paddingRight={5}
      paddingY={2}
    >
      <Box sx={{ position: "relative" }}>
        <Link to={routes.enter}>
          <Stack direction="row" alignItems="center" gap={3}>
            {!isRebrand && (
              <BunnyLogo
                height={50}
                width={50}
                url={isEducation ? BUNNY_EDUCATION_LOGO_URL : undefined}
              />
            )}
            <ClipboardLogo
              height={30}
              width={100}
              url={isEducation || !isDefined(profile?.type) ? JUST_CLIPBOARD_LOGO_URL : undefined}
            />
          </Stack>
        </Link>
        {isRebrand ? (
          <Box
            sx={{
              position: "absolute",
              top: -17,
              right: -22,
              pointerEvents: "none",
              zIndex: 1,
            }}
          >
            <EnvironmentChip />
          </Box>
        ) : null}
      </Box>

      {/* Primary context — workplace/worker name + local time. Admin-only:
          when impersonating, the chip is clickable and opens the matching
          details drawer (worker or workplace), matching the legacy
          name-affordance. */}
      {admin && <SessionContextChip onClick={onContextChipClick} />}

      <Stack direction="row" alignItems="center" gap={3} height="100%">
        {admin ? (
          <>
            <AccountSwitcherDesktop />

            <Box
              sx={(theme) => ({
                alignSelf: "stretch",
                width: "1px",
                marginY: 2,
                backgroundColor: theme.palette.navbar?.sectionDivider,
              })}
            />

            {isImpersonating && (
              <Tooltip title="Switch to admin">
                <span>
                  <IconButton
                    variant={navLinkVariant}
                    iconType="person-male-formal"
                    aria-label="Switch to admin"
                    onClick={switchToAdmin}
                  />
                </span>
              </Tooltip>
            )}

            {displayNotificationsButton && (
              <Tooltip title="Notifications">
                <span>
                  <Badge count={unreadNotificationCount}>
                    <IconButton
                      variant={navLinkVariant}
                      iconType={iconTypeNotificationsButton}
                      aria-label="Notifications"
                      onClick={onClickNotificationsButton}
                    />
                  </Badge>
                </span>
              </Tooltip>
            )}

            <ThemeModeToggle />
          </>
        ) : (
          <>
            {displayHelpButton ? (
              <Tooltip title="Help Center">
                <Button
                  variant={navLinkVariant}
                  endIconType={iconTypeHelpButton}
                  onClick={onClickHelpButton}
                >
                  Help
                </Button>
              </Tooltip>
            ) : (
              <ContactSupportButton />
            )}

            {displayNotificationsButton && (
              <Tooltip title="Notifications">
                <span>
                  <Badge count={unreadNotificationCount}>
                    <IconButton
                      variant={navLinkVariant}
                      iconType={iconTypeNotificationsButton}
                      aria-label="Notifications"
                      onClick={onClickNotificationsButton}
                    />
                  </Badge>
                </span>
              </Tooltip>
            )}

            <ThemeModeToggle />

            <AccountSwitcherDesktop />
          </>
        )}
      </Stack>
      <NotificationsCenterDrawer modalState={notificationsCenterModalState} />
      {admin && isImpersonatingWorkplace && (
        <FacilityDrawer
          userId={isWorkplaceDrawerOpen ? userId : undefined}
          closeDrawer={() => setIsWorkplaceDrawerOpen(false)}
          isAdmin={admin}
          ldFlags={ldFlags}
        />
      )}
      {admin && isImpersonatingWorker && (
        <WorkerDrawer
          userId={isWorkerDrawerOpen ? userId : undefined}
          closeDrawer={() => setIsWorkerDrawerOpen(false)}
          ldFlags={ldFlags}
        />
      )}
    </Stack>
  );
}
